import { useCallback, useEffect, useMemo, useState } from 'react'

import { any, compose, isNotNil, path } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { HistoryToggleOff, SmokingRooms } from '@mui/icons-material'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { gridClasses } from '@mui/x-data-grid-pro'

import { titleize } from 'inflection'
import { DateTime } from 'luxon'

import {
  ErrorMessage,
  Loading,
  MobileList,
  MobileListCardRow,
} from '@common/components'
import { getValueFromColumnDef } from '@common/components/MobileList'
import ReportModal from '@common/components/reports/ReportModal'
import { formatCurrency, useSmallScreen } from '@common/utils'
import { buildReasonsById } from '@common/utils/adjustmentReasonsUtils'
import AttachmentsModal from '@rest/pages/Properties/PropertyEvents/AttachmentsModal'
import EditFeedback from '@rest/pages/Properties/PropertyEvents/EditFeedback'
import NoiseFeedback from '@rest/pages/Properties/PropertyEvents/NoiseFeedback'
import PostCharge from '@rest/pages/Properties/PropertyEvents/PostCharge'
import EventComment from '@rest/pages/Units/EventComment'
import { List } from '@rest/UI/components'
import AdditionalOccurenciesModal from '@rest/UI/components/EventList/components/AdditionalOccurenciesModal'
import EventListActionCell from '@rest/UI/components/EventList/components/EventListActionCell'
import EventListCommentsCell from '@rest/UI/components/EventList/components/EventListCommentsCell'
import EventListCreatedOnCell from '@rest/UI/components/EventList/components/EventListCreatedOnCell'
import EventListLastModifiedCell from '@rest/UI/components/EventList/components/EventListLastModifiedCell'
import EventListNetChargeCell from '@rest/UI/components/EventList/components/EventListNetChargeCell'
import EventListRowDetail from '@rest/UI/components/EventList/components/EventListRowDetail'
import EventListTypeCell from '@rest/UI/components/EventList/components/EventListTypeCell'
import { usePrevilegeCheck } from '@rest/Utils/PrivilegeCheck'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {boolean} props.isHotel
 */
function EventsEmptyState({ isProperty, isHotel }) {
  const unitType = isHotel ? 'room' : 'unit'
  const entity = isProperty ? 'property' : unitType
  return (
    <Stack sx={{ mt: 5 }} alignItems="center" justifyContent="center">
      <SmokingRooms sx={{ fontSize: 60, mb: 2 }} />
      No events have been detected for this {entity} yet.
    </Stack>
  )
}

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.property
 * @param {string} [props.property.timezone]
 * @param {boolean} [props.property.liveSmokeAlerts]
 * @param {string} [props.property.propertyType]
 * @param {boolean} [props.property.reservationGrouping]
 * @param {string} [props.property.smokingReportVersion]
 * @param {string[]} [props.property.expandedFlags]
 * @param {number} [props.property.unitCount]
 * @param {Object} [props.unit]
 * @param {Object[]} [props.dateRange]
 */
export default function EventList({ dateRange, property, unit = undefined }) {
  const {
    doMarkEventListAsOutdated,
    doEventListSetPage,
    doEventListSetPageSize,
    doEventListSetOrdering,
    doEventListSetSearch,
    doEventListSetFilter,
    eventList,
    eventListRaw: { ordering = [], filter = {} },
    eventListIsLoading,
    currentRole: role,
    smokeAdjustmentReasons,
  } = useConnect(
    'doMarkEventListAsOutdated',
    'doEventListSetPage',
    'doEventListSetPageSize',
    'doEventListSetOrdering',
    'doEventListSetSearch',
    'doEventListSetFilter',
    'selectEventList',
    'selectEventListRaw',
    'selectEventListIsLoading',
    'selectCurrentRole',
    'selectSmokeAdjustmentReasons',
  )
  const isSmallScreen = useSmallScreen()

  const isHotel = property?.propertyType === 'HOTEL'
  const activePermissions = role?.permissions ?? []

  const [isNoise, setIsNoise] = useState(false)
  const [additionalOccurenciesData, setAdditionalOccurenciesData] = useState(null)

  useEffect(() => {
    setIsNoise(property?.expandedFlags?.includes('NOISE') || false)
    return () => {
      doEventListSetSearch('')
      doEventListSetOrdering(['createdOn', 'desc'])
    }
  }, [property, unit])

  useEffect(() => {
    if (property) {
      const startDate = dateRange[0] ? dateRange[0] : null
      const endDate = dateRange[1] ? dateRange[1] : null

      if (startDate && endDate && startDate < endDate) {
        let eventClasses = 'SMOKE'
        if (property.expandedFlags?.includes('NOISE')) {
          eventClasses += ',NOISE'
        }
        doEventListSetFilter({
          ...filter,
          startDate,
          endDate,
          eventClass: eventClasses,
        })
      }
    }
  }, [dateRange, property])

  const hasReservationData = useMemo(
    () =>
      any(compose(isNotNil, path(['reservation', 'confirmationNumber'])))(
        eventList.results,
      ),
    [eventList.results],
  )

  const attachmentsEnabled = usePrevilegeCheck({ flags: ['ATTACHMENTS'], property })

  const reasonsById = useMemo(
    () => buildReasonsById(smokeAdjustmentReasons),
    [smokeAdjustmentReasons],
  )

  const [adjustFormOpen, setAdjustFormOpen] = useState(false)
  const [postFormOpen, setPostFormOpen] = useState(false)
  const [noiseFeedbackOpen, setNoiseFeedbackOpen] = useState(false)
  const [eventReportOpen, setEventReportOpen] = useState(false)
  const [commentsFormOpen, setCommentsFormOpen] = useState(false)
  const [activeInstance, setActiveInstance] = useState(null)
  const [attachmentsModalData, setAttachmentsModalData] = useState(null)

  const detailRowContent = useCallback(
    ({ row }) =>
      row.childCount === 0 ? null : (
        <EventListRowDetail
          {...row}
          zone={property.timezone}
          isPrunit={property.unitCount === 1}
          onReportClick={(childRow, index) => {
            setEventReportOpen(true)
            setActiveInstance({
              ...childRow,
              eventPosition: index + 2, // start with 1 + parent event
            })
          }}
        />
      ),
    [property],
  )
  const detailRowHeight = useCallback(() => 'auto', [])

  const UNIT_NAME_COLUMN = {
    field: 'unitName',
    sortField: 'unit__name',
    headerName: isHotel ? 'Room' : 'Unit',
    flex: 0.15,
    sortable: true,
    renderMobile: ({ row }) => row.unitName,
    minWidth: 100,
  }

  const CREATED_ON_COLUMN = {
    field: 'createdOn',
    sortable: true,
    headerName: property?.liveSmokeAlerts ? 'Alert Sent' : 'Detected On',
    flex: 0.4,
    minWidth: 175,
    renderCell: ({ row: { createdOn, endTime } }) => (
      <EventListCreatedOnCell
        createdOn={createdOn}
        endTime={endTime}
        property={property}
      />
    ),
    renderMobile: ({ row: { createdOn, endTime } }) => (
      <EventListCreatedOnCell
        mobileView
        createdOn={createdOn}
        endTime={endTime}
        property={property}
      />
    ),
  }

  const COMMENTS_COLUMN = {
    field: 'comments',
    sortable: false,
    headerName: 'Comments',
    flex: 0.9,
    minWidth: 250,
    valueGetter: (_, row) => {
      if (row.eventClass === 'NOISE') {
        return row.noiseFeedback?.comments
      }
      return row.smokeFeedback?.comments
    },
    valueFormatter: (value) => value || '--',
    renderCell: (params) => (
      <EventListCommentsCell
        params={params}
        attachmentsEnabled={attachmentsEnabled}
        setAttachmentsModalData={setAttachmentsModalData}
      />
    ),
    renderMobile: (params) => (
      <EventListCommentsCell
        mobileView
        params={params}
        attachmentsEnabled={attachmentsEnabled}
        setAttachmentsModalData={setAttachmentsModalData}
      />
    ),
  }

  const LAST_MODIFIED_COLUMN = {
    field: 'feedback.modifiedByName',
    sortable: false,
    headerName: 'Last Modified',
    flex: 0.4,
    minWidth: 175,
    valueGetter: (_, row) =>
      row.eventClass === 'NOISE'
        ? row.noiseFeedback?.modifiedByName
        : row.smokeFeedback?.modifiedByName,
    valueFormatter: (value) => value ?? 'N/A',
    renderCell: (params) => (
      <EventListLastModifiedCell params={params} property={property} />
    ),
    renderMobile: (params) => (
      <EventListLastModifiedCell mobileView params={params} property={property} />
    ),
  }

  const NET_CHARGE_COLUMN = {
    field: 'feedback.netChargeAmount',
    headerName: 'Net Charge',
    sortable: false,
    type: 'number',
    flex: 0.4,
    minWidth: 125,
    valueGetter: (_, row) => ({
      netCharge:
        row.smokeFeedback?.netChargeAmount !== null
          ? Number(row.smokeFeedback?.netChargeAmount)
          : null,
      adjustedOn: row.smokeFeedback?.adjustedOn,
    }),
    valueFormatter: (value) => {
      const foremmatedValue = formatCurrency(value.netCharge, '--')
      const asterisk = value.adjustedOn ? '*' : ''
      return `${foremmatedValue}${asterisk}`
    },
    renderCell: (params) => (
      <EventListNetChargeCell params={params} reasonsById={reasonsById} />
    ),
    renderMobile: (params) => (
      <EventListNetChargeCell mobileView params={params} reasonsById={reasonsById} />
    ),
  }

  const ACTION_COLUMN = {
    field: 'actions',
    sortable: false,
    headerName: 'Actions',
    headerAlign: 'center',
    width: 260,
    renderCell: (params) => (
      <EventListActionCell
        params={params}
        setActiveInstance={setActiveInstance}
        setNoiseFeedbackOpen={setNoiseFeedbackOpen}
        setCommentsFormOpen={setCommentsFormOpen}
        setAdjustFormOpen={setAdjustFormOpen}
        setPostFormOpen={setPostFormOpen}
        setEventReportOpen={setEventReportOpen}
        activePermissions={activePermissions}
      />
    ),
  }

  const EVENT_TYPE_COLUMN = {
    field: 'eventClass',
    sortable: true,
    headerName: 'Type',
    flex: 0.15,
    renderCell: ({ row }) => <EventListTypeCell row={row} />,
  }

  const CONFIRMATION_NUMBER_COLUMN = {
    field: 'reservation.confirmationNumber',
    sortField: 'reservation__confirmation_number',
    sortable: true,
    headerName: 'Confirmation #',
    flex: 0.35,
    minWidth: 200,
    valueGetter: (_, row) => row.reservation?.confirmationNumber,
    valueFormatter: (value) => value || 'N/A',
    renderMobile: (params) => params.formattedValue,
  }

  const EVENT_CATEGORY_COLUMN = {
    field: 'eventType',
    sortable: false,
    headerName: '',
    flex: 0.01,
    renderCell: ({ row }) => {
      if (row.eventType !== 'EXTERNAL') {
        return (
          <Tooltip title="Guest has not checked out yet">
            <HistoryToggleOff />
          </Tooltip>
        )
      }
      return null
    },
  }

  const columns = useMemo(() => {
    const result = [
      CREATED_ON_COLUMN,
      COMMENTS_COLUMN,
      LAST_MODIFIED_COLUMN,
      NET_CHARGE_COLUMN,
      ACTION_COLUMN,
    ]

    if (property?.unitCount !== 1 && !unit) {
      result.unshift(UNIT_NAME_COLUMN)
    }
    if (property?.reservationGrouping || hasReservationData) {
      result.unshift(CONFIRMATION_NUMBER_COLUMN)
    }
    if (property?.reservationGrouping) {
      const CHECK_IN_COLUMN = {
        field: 'checkIn',
        sortable: true,
        headerName: 'Check In',
        flex: 0.3,
        minWidth: 125,
        valueGetter: (_, row) =>
          row.checkIn
            ? DateTime.fromISO(row.checkIn, {
                zone: property.timezone,
              }).toLocaleString(DateTime.DATE_MED)
            : null,
        valueFormatter: (value) => value ?? 'N/A',
        renderMobile: (params) => params.formattedValue,
      }
      const CHECK_OUT_COLUMN = {
        field: 'checkOut',
        sortable: true,
        headerName: 'Check Out',
        flex: 0.3,
        minWidth: 125,
        valueGetter: (_, row) =>
          row.checkOut
            ? DateTime.fromISO(row.checkOut, {
                zone: property.timezone,
              }).toLocaleString(DateTime.DATE_MED)
            : null,
        valueFormatter: (value) => value ?? 'N/A',
        renderMobile: (params) => params.formattedValue,
      }

      const checkInIndex = result.indexOf(CREATED_ON_COLUMN)
      result.splice(checkInIndex + 1, 0, CHECK_IN_COLUMN, CHECK_OUT_COLUMN)
    }

    if (isNoise) {
      result.unshift(EVENT_TYPE_COLUMN)
    }
    if (unit) {
      result.unshift(EVENT_CATEGORY_COLUMN)
    }

    return result
  }, [property, unit, hasReservationData, isNoise])

  const MobileListItem = useCallback(
    (row) => {
      const { eventClass } = row

      const filteredColumns = columns.filter(
        (column) => !['eventClass', 'eventType', 'actions'].includes(column.field),
      )
      return (
        <Stack spacing={0.5}>
          <Box display="flex" sx={{ '&&': { mb: 1 } }}>
            <Box display="flex" alignItems="center">
              {row.eventType !== 'EXTERNAL' && (
                <HistoryToggleOff sx={{ mr: 0.5, fontSize: 18 }} />
              )}
              <EventListTypeCell mobileView row={row} />
              <Typography variant="caption" fontWeight="bold" ml={0.5}>
                {titleize(eventClass)} Event
              </Typography>
            </Box>
          </Box>
          {filteredColumns.map((column) => {
            const value = getValueFromColumnDef({ row, column })
            return (
              <MobileListCardRow
                key={column.field}
                label={column.headerName}
                value={value}
              />
            )
          })}
          <MobileListCardRow
            label="Additional Occurencies"
            value={
              row.childCount ? (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() =>
                    setAdditionalOccurenciesData({
                      row,
                      property,
                      reportActions: { setEventReportOpen, setActiveInstance },
                    })
                  }
                >
                  View
                </Typography>
              ) : null
            }
          />
          <Box sx={{ '&&': { mt: 2, mb: 0.5 } }}>
            <EventListActionCell
              params={{ row }}
              setActiveInstance={setActiveInstance}
              setNoiseFeedbackOpen={setNoiseFeedbackOpen}
              setCommentsFormOpen={setCommentsFormOpen}
              setAdjustFormOpen={setAdjustFormOpen}
              setPostFormOpen={setPostFormOpen}
              setEventReportOpen={setEventReportOpen}
              activePermissions={activePermissions}
            />
          </Box>
        </Stack>
      )
    },
    [columns],
  )

  if (eventListIsLoading && !eventList) {
    return <Loading />
  }

  if (!eventListIsLoading && !eventList) {
    return <ErrorMessage>Oops. Something went wrong</ErrorMessage>
  }

  return (
    <>
      {/* Dialogs Section Start */}
      {commentsFormOpen && (
        <EventComment
          propertyId={activeInstance.property}
          eventId={activeInstance.id}
          isSmokeEvent={activeInstance.eventClass === 'SMOKE'}
          currentFeedback={activeInstance.smokeFeedback ?? activeInstance.noiseFeedback}
          onClose={(success) => {
            setCommentsFormOpen(false)
            if (success === true) {
              doMarkEventListAsOutdated()
            }
          }}
        />
      )}
      {adjustFormOpen ? (
        <EditFeedback
          instance={activeInstance}
          property={property}
          onClose={(success) => {
            setAdjustFormOpen(false)
            if (success === true) {
              doMarkEventListAsOutdated()
            }
          }}
        />
      ) : null}
      {postFormOpen ? (
        <PostCharge
          instance={activeInstance}
          property={property}
          onClose={(success) => {
            setPostFormOpen(false)
            if (success === true) {
              doMarkEventListAsOutdated()
            }
          }}
        />
      ) : null}
      {noiseFeedbackOpen ? (
        <NoiseFeedback
          propertyId={activeInstance.property}
          eventId={activeInstance.id}
          existingFeedback={activeInstance.noiseFeedback}
          onClose={(success) => {
            setNoiseFeedbackOpen(false)
            if (success === true) {
              doMarkEventListAsOutdated()
            }
          }}
        />
      ) : null}
      {eventReportOpen && (
        <ReportModal
          instance={activeInstance}
          property={property}
          onClose={() => setEventReportOpen(false)}
        />
      )}
      <AttachmentsModal
        open={!!attachmentsModalData}
        attachments={attachmentsModalData}
        onClose={() => setAttachmentsModalData(null)}
      />
      <AdditionalOccurenciesModal
        open={!!additionalOccurenciesData}
        onClose={() => setAdditionalOccurenciesData(null)}
        data={additionalOccurenciesData}
      />
      {/* Dialogs Section End */}

      {isSmallScreen.mobile ? (
        <MobileList
          data-testid="events_list"
          title="Events"
          rows={eventList?.results ?? []}
          itemBuilder={MobileListItem}
          page={eventList?.current ?? 0}
          pageChange={doEventListSetPage}
          pageSize={eventList?.pageSize}
          pageSizeChange={doEventListSetPageSize}
          rowCount={eventList?.count ?? 0}
          setSearch={doEventListSetSearch}
          pageSizeOptions={[25]}
          hideFooter={(eventList?.numPages ?? 0) <= 1}
          loading={eventListIsLoading}
          slots={{
            noRowsOverlay: () => EventsEmptyState({ isProperty: !unit, isHotel }),
          }}
        />
      ) : (
        <List
          autoHeight
          disableColumnMenu
          data-testid="events_list"
          columns={columns}
          rows={eventList?.results ?? []}
          page={eventList?.current ?? 0}
          pageChange={doEventListSetPage}
          pageSize={eventList?.pageSize}
          pageSizeChange={doEventListSetPageSize}
          rowCount={eventList?.count ?? 0}
          sortChange={doEventListSetOrdering}
          setSearch={doEventListSetSearch}
          pageSizeOptions={[25]}
          hideFooter={(eventList?.numPages ?? 0) <= 1}
          loading={eventListIsLoading}
          getDetailPanelContent={detailRowContent}
          getDetailPanelHeight={detailRowHeight}
          currentOrdering={ordering ?? []}
          slots={{
            noRowsOverlay: () => EventsEmptyState({ isProperty: !unit, isHotel }),
          }}
          pinnedColumns={{ right: ['actions'] }}
          sx={{
            [`& .${gridClasses.detailPanel}`]: {
              backgroundColor: 'transparent',
            },
            [`& .${gridClasses.row}`]: {
              '&:hover .MuiDataGrid-cell--pinnedRight': {
                backgroundColor: `#f0f0f0`,
              },
            },
            '& .MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-cell--pinnedRight':
              {
                backgroundColor: `grey.50`,
                mt: 0.1,
                '&.MuiDataGrid-columnHeader--pinnedRight': {
                  borderLeft: 'none',
                },
              },
          }}
        />
      )}
    </>
  )
}
